import React, { useMemo, useRef, useState } from "react";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../../lib/util/common-util";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import { CMButtonPopupSearchConditionDateRange } from "../../button-popup";
import { compareYmd, ymdToText } from "../../../../lib/util/common-date-util";

export type CMButtonSearchConditionDateRangeProps = {
  label: string;
  placeholderDate?: {
    start: string;
    end: string;
  };
  value?: {
    start?: {
      year: number;
      month: number;
      day: number;
    };
    end?: {
      year: number;
      month: number;
      day: number;
    };
  };
  minDate?: {
    year: number;
    month: number;
    day: number;
  };
  maxDate?: {
    year: number;
    month: number;
    day: number;
  };

  hideClearButton?: boolean;

  /**
   * 開始日時と終了日時の区切り文字として使用する。
   */
  separatorRange: string;
  labelSubmit: string;
  labelClear: string;

  onChange?: (value: {
    start?: {
      year: number;
      month: number;
      day: number;
    };
    end?: {
      year: number;
      month: number;
      day: number;
    };
  }) => void;
} & ComponentStyleProps;

export const CMButtonSearchConditionDateRange = ({
  label,
  placeholderDate = { start: "", end: "" },

  value = {},

  minDate,
  maxDate,

  hideClearButton = false,

  separatorRange,
  labelSubmit,
  labelClear,

  onChange,

  sx,
  ...rest
}: CMButtonSearchConditionDateRangeProps) => {
  const styles = useStyleConfig("CMButtonSearchConditionDateRangeStyles");

  const displayValue = useMemo(() => {
    return dateRangeToText(value ?? undefined, separatorRange);
  }, [value, separatorRange]);

  // ポップアップエリアの表示制御に使用する
  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const [startDateInternal, setStartDateInternal] = useState<{
    year: number;
    month: number;
    day: number;
  } | null>(value.start ?? null);
  const [endDateInternal, setEndDateInternal] = useState<{
    year: number;
    month: number;
    day: number;
  } | null>(value.end ?? null);

  const resetAllInternalState = (
    newValue: {
      start?: {
        year: number;
        month: number;
        day: number;
      };
      end?: {
        year: number;
        month: number;
        day: number;
      };
    } = value,
  ) => {
    setStartDateInternal(newValue.start ?? null);
    setEndDateInternal(newValue.end ?? null);
  };

  const handleChangeInternalValue = (
    value: { year: number; month: number; day: number } | null,
    on: "start" | "end",
  ) => {
    switch (on) {
      case "start":
        setStartDateInternal(value);
        break;
      case "end":
        setEndDateInternal(value);
        break;
    }
  };

  const handleSubmit = () => {
    // もしstartとendの大小関係が逆であれば、逆転させて返却する。
    const submittedValue =
      hasValue(startDateInternal) &&
      hasValue(endDateInternal) &&
      compareYmd(startDateInternal, endDateInternal) > 0
        ? { start: endDateInternal, end: startDateInternal }
        : {
            start: startDateInternal ?? undefined,
            end: endDateInternal ?? undefined,
          };

    onClosePopup();
    resetAllInternalState(submittedValue);
    onChange?.(submittedValue);
  };

  const handleClear = () => {
    onClosePopup();
    resetAllInternalState({});
    onChange?.({});
  };

  const ref = useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={displayValue}
      onClick={onTogglePopup}
      isOpenPopup={isOpenPopup}
      sx={{ ...styles, ...sx }}
      {...rest}
      ref={ref}
    >
      <CMButtonPopupSearchConditionDateRange
        placeholder={placeholderDate}
        separatorRange={separatorRange}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        minDate={minDate}
        maxDate={maxDate}
        hideClearButton={hideClearButton}
        startDateInternal={startDateInternal}
        endDateInternal={endDateInternal}
        onChangeInternalValue={handleChangeInternalValue}
        onSubmit={handleSubmit}
        onClear={handleClear}
      />
    </CMButtonSearchCondition>
  );
};

export const CMButtonSearchConditionDateRangeStyles: ComponentSingleStyleConfig =
  {
    baseStyle: {},
  };

/**
 * 日付(ymd型)を受け取って、文字列を返却する。
 * 開始日時と終了日時が設定されている場合、区切り文字で結合した文字列を返却する。
 * 全体が値なし、または開始も終了も値なしであれば、nullを返却する。
 */
function dateRangeToText(
  value?: {
    start?: {
      year: number;
      month: number;
      day: number;
    } | null;
    end?: {
      year: number;
      month: number;
      day: number;
    } | null;
  },
  separatorRange?: string,
): string | null {
  if (isNullish(value)) {
    return null;
  }
  if (isNullish(value.start) && isNullish(value.end)) {
    return null;
  }

  const startStr = hasValue(value.start) ? ymdToText(value.start) : undefined;

  const endStr = hasValue(value.end) ? ymdToText(value.end) : undefined;

  if (hasValue(startStr) && hasValue(endStr)) {
    return startStr + separatorRange + endStr;
  } else if (hasValue(startStr)) {
    return startStr + separatorRange;
  } else if (hasValue(endStr)) {
    return separatorRange + endStr;
  } else {
    return null;
  }
}
